﻿import {Core} from "../../Base/Core";
import {UIButton} from "../Tools/Base/UIButton";
import {UIText} from "../Tools/Base/UIText";
import {UICommonPopup} from "../Tools/Base/UICommonPopup";
import {UISprite} from "../Tools/Base/UISprite";
import {Scrollbox} from "pixi-scrollbox";

class LineSplitter extends PIXI.Graphics {

    constructor(n = 1) {
        super();
        this.n = Math.max(1, ~~n);
        this.fitLayout(new PIXI.Rectangle(-5, 0, 10, 50));
    }

    fitLayout(rect) {
        this.position.set(rect.x + rect.width * 0.5, rect.y);

        let thickness = Core.app.viewport.width * 0.01;
        this.clear().lineStyle({
            width: thickness,
            color: 0xFFFFFF,
            alpha: 1,
            alignment: 0.5,
            cap: PIXI.LINE_CAP.ROUND
        });

        let k = 0.3;
        let step = rect.width / this.n;
        let width = step * (this.n - 1);
        for (let i = 0; i < this.n; i++) {
            let x = -width * 0.5 + step * i;
            this.moveTo(x * k, 0).lineTo(x, rect.height);
        }
    }
}

class CollectionCreature extends UISprite {
    Awake() {
        super.Awake();
        this.creatureId = 0;
        this.title = this.addChild(new UIText("", {
            fontFamily: "-apple-system,Roboto,Helvetica,Arial,sans-serif",
            fontWeight: "normal",
            dropShadow: false,
            color: 0xCCCCCC,
        }));
        this.picture = this.addChild(new UISprite("questionmark-ico-black-alpha"));
        this.splitter = this.addChild(new LineSplitter());
    }

    Init(id, splitter = true) {
        this.creatureId = id;
        this.splitter.SetActive(splitter);
        this.Update();
    }

    Update() {
        let creatureData = Core.GameData.Creatures.Get(this.creatureId);
        let unlocked = creatureData && Core.Player.CollectionCreatures.includes(creatureData.id);
        this.title.SetActive(unlocked);
        this.title.text = unlocked ? Core.Localizator.Get(creatureData.creatureName) : "";
        this.picture.setImage(unlocked ? `creatures/${this.creatureId}` : "questionmark-ico-black-alpha");
    }

    fitLayout(rect) {
        rect = rect.clone();
        this.position.copyFrom(rect);
        rect.x = rect.y = 0;

        let titleRect = rect.clone();
        titleRect.height = rect.height * 0.1;
        this.title.autoFitProps.autoFitPadding = titleRect.height * 0.05;
        this.title.fitLayout(titleRect);

        let creatureRect = rect.clone();
        creatureRect.height = rect.height * 0.7;
        creatureRect.y = titleRect.bottom;
        this.picture.fitRect(creatureRect);

        let splitterRect = rect.clone().pad(0, -rect.height * 0.45);
        splitterRect.y = creatureRect.bottom;
        this.splitter.fitLayout(splitterRect);
    }
}

class CollectionEpoch extends UISprite {

    Awake() {
        super.Awake();
        this.epochId = 0;
        this.title = this.addChild(new UIText(""));
        this.creatures = [];
        this.nextEpochs = [];
    }

    Init(id) {
        this.epochId = id;
        let epochData = Core.GameData.Epochs.GetById(this.epochId);
        if (!epochData) return;

        let nextEpochs = epochData.nextEpochs || [];

        this.title.SetActive(epochData.epochLevel > 1);
        this.title.text = Core.Localizator.Get(epochData.epochName);

        let creatures = Core.GameData.Epochs.GetById(this.epochId).creatures;
        for (let creatureId of creatures) {
            let creature = this.addChild(new CollectionCreature());
            creature.Init(creatureId, nextEpochs.length > 0);
            this.creatures.push(creature);
        }

        for (let epochId of nextEpochs) {
            let epoch = this.addChild(new CollectionEpoch());
            epoch.Init(epochId);
            this.nextEpochs.push(epoch);
        }
        if (nextEpochs.length > 1) {
            this.circle = this.addChild(new UISprite("circle-white"));
            this.splitter = this.addChild(new LineSplitter(nextEpochs.length));
        }

        this.Update();
    }

    Update() {
        for (let creature of this.creatures) {
            creature.Update();
        }
        for (let epoch of this.nextEpochs) {
            epoch.Update();
        }
    }

    fitLayout(rect) {
        // We take into account only rect width and update rect height by reference
        // So we can finally resize the scroll area

        // Center everything in provided rect and pin content to top
        this.position.set(rect.x + rect.width * 0.5, rect.y);

        let epochRect = rect.clone();
        epochRect.x = -rect.width * 0.5;
        epochRect.y = 0;

        // Width of creatures column
        let epochWidth = Math.min(epochRect.width, Core.app.viewport.width / 6);

        // All creatures space. Will be increased with each creature
        let currentEpochRect = new PIXI.Rectangle(-epochWidth * 0.5, 0, epochWidth, 0);

        if (this.title.Active) {
            let titleRect = new PIXI.Rectangle(
                currentEpochRect.left, currentEpochRect.top,
                currentEpochRect.width, currentEpochRect.width * 0.3
            );
            this.title.autoFitProps.autoFitPadding = titleRect.height * 0.05;
            this.title.fitLayout(titleRect);
            currentEpochRect.height += titleRect.height;
        }

        // One creature size in current epoch
        let creatureRect = currentEpochRect.clone();
        creatureRect.height = creatureRect.width * 2;
        creatureRect.y = currentEpochRect.height;
        for (let creature of this.creatures) {
            creature.fitLayout(creatureRect);
            creatureRect.y += creatureRect.height;
            currentEpochRect.height += creatureRect.height;
        }

        let splitterPadding = currentEpochRect.width * 0.2;
        let splitterRect = epochRect.clone().pad(-epochRect.width * 0.2, 0);
        splitterRect.y = currentEpochRect.bottom + splitterPadding;
        splitterRect.height = currentEpochRect.width * 0.3;

        if (this.circle) {
            this.circle.fitRect(splitterRect);
            splitterRect.y += splitterRect.height + splitterPadding;
            currentEpochRect.height += splitterRect.height + splitterPadding;
        }

        if (this.splitter) {
            this.splitter.fitLayout(splitterRect);
            currentEpochRect.height += splitterRect.height + splitterPadding;
        }
        currentEpochRect.height += splitterPadding;

        rect.height += currentEpochRect.bottom;

        let nextEpochRect = epochRect.clone();
        nextEpochRect.y = currentEpochRect.bottom;
        nextEpochRect.width = epochRect.width / Math.max(1, this.nextEpochs.length);
        let nextEpochsHeight = nextEpochRect.height = 0;
        for (let epoch of this.nextEpochs) {
            nextEpochRect.height = 0;
            epoch.fitLayout(nextEpochRect);
            nextEpochRect.x += nextEpochRect.width;
            nextEpochsHeight = Math.max(nextEpochsHeight, nextEpochRect.height);
        }

        rect.height += nextEpochsHeight;
    }
}

export class UICollection extends UICommonPopup {

    Awake() {
        super.Awake({
            title: Core.Localizator.Get("collection"),
            closeButton: true,
            masked: true
        });

        this.settingsBtn = this.addChild(new UIButton("settings-ico"));
        this.progressField = this.content.addChild(new UIText());
        this.scrollbox = this.content.addChildAt(new Scrollbox({
            overflow: "hidden",
            interaction: Core.app.renderer.plugins.interaction,
        }), 0);
        this.rootEpoch = this.scrollbox.content.addChild(new CollectionEpoch());

        this.settingsBtn.on("pointertap", () => {
            this.Hide();
            Core.UI.settings.Show();
        });
    }

    Init() {
        super.Init();
        let firstEpoch = Core.GameData.Epochs.epochsByLevel.get(1)[0];
        this.rootEpoch.Init(firstEpoch.id);
    }

    fitLayout(viewport = Core.app.viewport) {
        this.setPopupRect(viewport);
        super.fitLayout(viewport);

        let contentRect = this.getContentSize();
        let headerRect = new PIXI.Rectangle(this.header.x, this.header.y, this.header.width, this.header.height);

        this.settingsBtn.scale.set(headerRect.height * 0.8 / this.settingsBtn.texture.height);
        this.settingsBtn.position.set(
            headerRect.x + headerRect.width * 0.05 + this.settingsBtn.width * 0.5,
            headerRect.y + headerRect.height * 0.5
        );

        let progressRect = contentRect.clone();
        progressRect.height = contentRect.width * 0.05;
        progressRect.y = headerRect.height;
        this.progressField.fitLayout(progressRect);

        let epochRect = contentRect.clone();
        epochRect.y = progressRect.bottom;
        epochRect.height = 0; // This will be updated recursively to finally collect total scroll area height
        this.rootEpoch.fitLayout(epochRect);

        this.scrollbox.resize({
            boxWidth: contentRect.width,
            boxHeight: contentRect.height,
            scrollWidth: contentRect.width,
            scrollHeight: epochRect.bottom,
        });
    }

    Show() {
        this.Update();
        super.Show();
    }

    Update() {
        let inCollection = Core.Player.PlanetsSave.collection.length;
        let overall = Core.GameData.Creatures.creatures.length;
        this.progressField.text = Core.Localizator.Get("collection_progress", inCollection, overall);
        this.rootEpoch.Update();
    }
}
